/**
 * Logger Type Definitions
 *
 * Defines the Logger interface and related types for both server and client logging.
 * Uses LogLayer as the underlying logging framework with Pino (server) and Datadog (client) transports.
 *
 * Config types are inferred from schemas in ./schema.ts (single source of truth).
 */

// Re-export config types from schema
export type { ClientLogsConfig, ServerLogsConfig } from './schema'

/**
 * Log levels in order of severity (lowest to highest)
 */
export enum LogLevel {
    trace = 'trace',
    debug = 'debug',
    info = 'info',
    warn = 'warn',
    error = 'error',
    fatal = 'fatal',
}

/**
 * Context information that can be attached to log messages
 * Provides structure for categorizing and filtering logs
 */
export type LoggerContext = Record<string, unknown>

type LogMessage = string | number | null | undefined

/**
 * Log level methods shared by Logger and LogBuilder
 */
interface LogLevelMethods {
    /** Logs a message at the info level */
    info(...messages: LogMessage[]): void
    /** Logs a message at the warn level */
    warn(...messages: LogMessage[]): void
    /** Logs a message at the error level */
    error(...messages: LogMessage[]): void
    /** Logs a message at the debug level */
    debug(...messages: LogMessage[]): void
    /** Logs a message at the trace level */
    trace(...messages: LogMessage[]): void
    /** Logs a message at the fatal level */
    fatal(...messages: LogMessage[]): void
}

/**
 * LogBuilder interface for chaining metadata and errors with log messages
 */
export interface LogBuilder extends LogLevelMethods {
    /**
     * Adds metadata to the log entry.
     * @param metadata - Arbitrary metadata to attach.
     * @returns The LogBuilder instance for chaining.
     */
    withMetadata(metadata: Record<string, unknown>): this
    /**
     * Adds an error to the log entry.
     * @param error - The error to attach.
     * @returns The LogBuilder instance for chaining.
     */
    withError(error: Error): this
}

/**
 * Core Logger interface
 *
 * Supports structured logging with context, metadata, and error handling.
 * Designed to work across both server and client environments.
 */
export interface Logger extends LogLevelMethods {
    /**
     * Returns a new Logger with the provided prefix applied to all messages.
     * @param prefix - The prefix to prepend to each log message.
     * @returns A new Logger with the prefix applied.
     */
    withPrefix(prefix: string): Logger
    /**
     * Appends context data which will be included with every log entry.
     * @param context - The context object to include with each log entry.
     * @returns A new Logger with the context applied.
     */
    withContext(context: LoggerContext): Logger
    /**
     * Returns the current context used by this Logger instance.
     * @returns The current LoggerContext.
     */
    getContext(): LoggerContext
    /**
     * Specifies metadata to include with the next log message.
     * @param metadata - Arbitrary metadata to include.
     * @returns A LogBuilder for further configuration and logging.
     */
    withMetadata(metadata: Record<string, unknown>): LogBuilder
    /**
     * Specifies an Error to include with the next log message.
     * @param error - The error object to include.
     * @returns A LogBuilder for further configuration and logging.
     */
    withError(error: Error): LogBuilder
    /**
     * Creates a new child Logger initialized with the supplied context applied.
     * @param context - The context to initialize the child Logger with.
     * @returns A new Logger instance with the given context.
     */
    getLogger(context: LoggerContext): Logger
    /**
     * Logs only the error object without a log message.
     * @param error - The error object to log.
     * @param opts - Optional log level override.
     */
    errorOnly(error: Error, opts?: { level?: LogLevel }): void
    /**
     * Logs only metadata without a log message.
     * @param metadata - The metadata to log.
     * @param logLevel - Optional log level override.
     */
    metadataOnly(metadata: Record<string, unknown>, logLevel?: LogLevel): void
}
